import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { knowledgeBaseAPI } from 'api/knowledge-base';
import {
  CreateKnowledgeBaseFolder,
  KnowledgeBaseFolder,
  KnowledgeBaseFolderLite,
  UpdateKnowledgeBaseFolder,
  CreateKnowledgeBaseAttachment,
  KnowledgeBaseAttachment,
  KnowledgeBaseTreeFolder,
  WebsiteContentResponse,
  AddWebsiteToKnowledgeBasePayload,
  AddAiPagesPayload,
  UpdateAiPagePayload,
} from 'api/knowledge-base/types';
import { oneHour } from '../constants';

export const KNOWLEDGE_BASE_FOLDERS_QUERY_KEY = ['knowledge-base-folders'];
export const KNOWLEDGE_BASE_FOLDER_QUERY_KEY = (folderId: number) => [
  'knowledge-base-folder',
  folderId,
];
export const KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY = (folderId: number) => [
  'knowledge-base-folder-tree',
  folderId,
];

export const useKnowledgeBaseFoldersQuery = (
  queryOptions?: UseQueryOptions<
    KnowledgeBaseFolderLite[],
    unknown,
    KnowledgeBaseFolderLite[],
    typeof KNOWLEDGE_BASE_FOLDERS_QUERY_KEY
  >,
): UseQueryResult<KnowledgeBaseFolderLite[], unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: KNOWLEDGE_BASE_FOLDERS_QUERY_KEY,
    queryFn: () => knowledgeBaseAPI.getKnowledgeBaseFolders(),
    staleTime: oneHour,
  });
};

export const useKnowledgeBaseFolderQuery = (
  folderId: number,
  queryOptions?: UseQueryOptions<
    KnowledgeBaseFolder,
    unknown,
    KnowledgeBaseFolder,
    ReturnType<typeof KNOWLEDGE_BASE_FOLDER_QUERY_KEY>
  >,
): UseQueryResult<KnowledgeBaseFolder, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: KNOWLEDGE_BASE_FOLDER_QUERY_KEY(folderId),
    queryFn: () => knowledgeBaseAPI.getKnowledgeBaseFolder(folderId),
    staleTime: oneHour,
  });
};

export const useCreateKnowledgeBaseFolderMutation = (
  options: UseMutationOptions<
    KnowledgeBaseFolder,
    unknown,
    CreateKnowledgeBaseFolder
  > = {},
): UseMutationResult<
  KnowledgeBaseFolder,
  unknown,
  CreateKnowledgeBaseFolder
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateKnowledgeBaseFolder) =>
      knowledgeBaseAPI.createKnowledgeBaseFolder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDERS_QUERY_KEY,
      });
    },
    ...options,
  });
};

export const useUpdateKnowledgeBaseFolderMutation = (
  options: UseMutationOptions<
    KnowledgeBaseFolder,
    unknown,
    { folderId: number; payload: UpdateKnowledgeBaseFolder }
  > = {},
): UseMutationResult<
  KnowledgeBaseFolder,
  unknown,
  { folderId: number; payload: UpdateKnowledgeBaseFolder }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderId,
      payload,
    }: {
      folderId: number;
      payload: UpdateKnowledgeBaseFolder;
    }) => knowledgeBaseAPI.updateKnowledgeBaseFolder(folderId, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDERS_QUERY_KEY,
      });

      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDER_QUERY_KEY(variables.folderId),
      });
    },
    ...options,
  });
};

export const useDeleteKnowledgeBaseFolderMutation = (
  options: UseMutationOptions<
    unknown,
    unknown,
    { folderId: number; rootFolderId?: number }
  > = {},
): UseMutationResult<
  unknown,
  unknown,
  { folderId: number; rootFolderId?: number }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId }: { folderId: number; rootFolderId?: number }) =>
      knowledgeBaseAPI.deleteKnowledgeBaseFolder(folderId),
    onSuccess: (_, { folderId, rootFolderId }) => {
      if (rootFolderId == null) {
        queryClient.invalidateQueries({
          queryKey: KNOWLEDGE_BASE_FOLDERS_QUERY_KEY,
        });

        queryClient.removeQueries({
          queryKey: KNOWLEDGE_BASE_FOLDER_QUERY_KEY(folderId),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(rootFolderId),
        });
      }
    },
    ...options,
  });
};

export const useDeleteKnowledgeBaseFolderContentMutation = (
  options: UseMutationOptions<
    unknown,
    unknown,
    { folderId: number; contentId: number; rootFolderId: number }
  > = {},
): UseMutationResult<
  unknown,
  unknown,
  { folderId: number; contentId: number; rootFolderId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderId,
      contentId,
    }: {
      folderId: number;
      contentId: number;
    }) =>
      knowledgeBaseAPI.deleteKnowledgeBaseFolderContent(folderId, contentId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(variables.rootFolderId),
      });
    },
    ...options,
  });
};
export const useKnowledgeBaseFolderTreeQuery = (
  folderId: number,
  queryOptions?: UseQueryOptions<
    KnowledgeBaseTreeFolder,
    unknown,
    KnowledgeBaseTreeFolder,
    ReturnType<typeof KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY>
  >,
): UseQueryResult<KnowledgeBaseTreeFolder, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(folderId),
    queryFn: () => knowledgeBaseAPI.getKnowledgeBaseFolderTree(folderId),
    staleTime: oneHour,
    refetchInterval: oneHour,
  });
};

export const useCreateKnowledgeBaseAttachmentMutation = (
  options: UseMutationOptions<
    KnowledgeBaseAttachment,
    unknown,
    { folderId: number; file: File; info: CreateKnowledgeBaseAttachment }
  > = {},
): UseMutationResult<
  KnowledgeBaseAttachment,
  unknown,
  { folderId: number; file: File; info: CreateKnowledgeBaseAttachment }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderId,
      file,
      info,
    }: {
      folderId: number;
      file: File;
      info: CreateKnowledgeBaseAttachment;
    }) => knowledgeBaseAPI.createKnowledgeBaseAttachment(folderId, file, info),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(variables.folderId),
      });
    },
    ...options,
  });
};

export const GET_CONTENT_FROM_WEBSITE_URL_QUERY_KEY = (url: string) => [
  'website-content',
  url,
];
export const useGetContentFromWebsiteUrlQuery = (
  url: string,
  queryOptions?: Omit<
    UseQueryOptions<
      WebsiteContentResponse,
      unknown,
      WebsiteContentResponse,
      ReturnType<typeof GET_CONTENT_FROM_WEBSITE_URL_QUERY_KEY>
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<WebsiteContentResponse, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: GET_CONTENT_FROM_WEBSITE_URL_QUERY_KEY(url),
    queryFn: () => knowledgeBaseAPI.getContentFromWebsiteUrl(url),
  });
};

export const useAddWebsiteToKnowledgeBaseMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    AddWebsiteToKnowledgeBasePayload
  > = {},
): UseMutationResult<void, unknown, AddWebsiteToKnowledgeBasePayload> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddWebsiteToKnowledgeBasePayload) =>
      knowledgeBaseAPI.addWebsiteToKnowledgeBase(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(variables.folder_id),
      });
    },
    ...options,
  });
};

export const useAddAiPagesMutation = (
  options: UseMutationOptions<void, unknown, AddAiPagesPayload> = {},
): UseMutationResult<void, unknown, AddAiPagesPayload> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddAiPagesPayload) =>
      knowledgeBaseAPI.addAiPages(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(variables.folder_id),
      });
    },
    ...options,
  });
};

export const useUpdateAiPageMutation = (
  options: UseMutationOptions<void, unknown, UpdateAiPagePayload> = {},
): UseMutationResult<void, unknown, UpdateAiPagePayload> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateAiPagePayload) =>
      knowledgeBaseAPI.updateAiPage(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASE_FOLDER_TREE_QUERY_KEY(variables.folder_id),
      });
    },
    ...options,
  });
};
